import type { IScope } from "angular";
import { finalize } from "rxjs/operators";
import { duration, utc } from "moment-timezone";
import { cloneDeep, find, isEmpty, remove } from "lodash";
import "@progress/kendo-ui/js/kendo.timepicker";

import type {
    IConfigurationGroupCare,
    IDaySchedule,
    IResponseWithLoading,
    IScheduleTimePeriod,
} from "App/Modules/Models";
import { DayType, DayTypeFunctions } from "App/Modules/Models";
import type { ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

const ENTER_KEY_CODE = 13;

export default class ConfigurationGroupCareEditComponentController {
    public static $inject: string[] = ["$scope", "mmSitesRepository"];

    public original: IConfigurationGroupCare;

    public modified: IConfigurationGroupCare;

    public dayTypes = DayTypeFunctions.WeekDaysMap();

    public editResponse: IResponseWithLoading<IConfigurationGroupCare>;

    public deleteResponse: IResponseWithLoading<void>;

    public resolve: IFormContainerResolve<IConfigurationGroupCare, void>;

    public scheduleForm: {
        showAdd: boolean;
        newTimePeriodDay: string;
        timePeriod: Date;
    };

    constructor(private readonly $scope: IScope, private readonly sitesRepository: ISitesRepository) {}

    public $onInit() {
        this.original = this.resolve.formData;
        this.modified = cloneDeep(this.original);

        this.scheduleForm = {
            newTimePeriodDay: "-1",
            showAdd: false,
            timePeriod: null,
        };
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public addTimePeriod() {
        let dtArray: DayType[];
        if (this.scheduleForm.newTimePeriodDay === "-1") {
            dtArray = [
                DayType.Monday,
                DayType.Tuesday,
                DayType.Wednesday,
                DayType.Thursday,
                DayType.Friday,
                DayType.Saturday,
                DayType.Sunday,
            ];
        } else {
            dtArray = [Number(this.scheduleForm.newTimePeriodDay)];
        }

        const m = utc(this.scheduleForm.timePeriod).local();
        // m.add(m.utcOffset(), "minutes");
        const start = duration(m.format("HH:mm"));
        const stop = duration(m.add(1, "minute").format("HH:mm"));

        // eslint-disable-next-line no-restricted-syntax
        for (const dt of dtArray) {
            const ds: IDaySchedule = find(this.modified.schedule.weeklySchedule.daySchedules, ["dayType", dt]);
            if (ds.scheduleTimePeriods.filter((x) => x.startTime.toISOString() === start.toISOString()).length === 0) {
                ds.scheduleTimePeriods.push({
                    day: dt,
                    dayScheduleId: null,
                    id: null,
                    startTime: start,
                    stopTime: stop,
                });
            }
        }

        this.scheduleForm.showAdd = false;
    }

    public deleteTimePeriod(timePeriod: IScheduleTimePeriod) {
        const ds: IDaySchedule = find(this.modified.schedule.weeklySchedule.daySchedules, [
            "dayType",
            timePeriod.day as any,
        ]);
        remove(ds.scheduleTimePeriods, timePeriod);
    }

    public save() {
        const delta = PatchDelta.difference(this.original, this.modified);

        if (!isEmpty(delta)) {
            extendWithLoadingAndFail(
                this.sitesRepository.patchConfigurationGroupCare(this.original.siteId, this.original.id, delta),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<IConfigurationGroupCare>) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.resolve.options.onCancel();
        }
    }

    public delete() {
        extendWithLoadingAndFail(this.sitesRepository.deleteConfigurationGroup(this.original.siteId, this.original.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;

                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (error) => {
                    this.deleteResponse = error;
                },
            );
    }

    public onTimePickerKeyPress(event: JQueryEventObject) {
        if (event.which !== ENTER_KEY_CODE) {
            return;
        }

        this.addTimePeriod();

        event.preventDefault();
    }
}
